import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, CornerDownLeft } from 'lucide-react';
import { USER_TYPE_INFO } from '@shared/user-types';
import type { OwnerOption } from '@shared/schemas/admin-access';
import { fetchImpersonationPreview } from '../../api/admin';
import type { ApiFetch } from '../../lib/apiFetchJson';
import { useApi } from '../../lib/api';
import { formatRelativeTime } from '../account-view/timeline-helpers';
import { useDebouncedValue } from '../flow/useDebouncedValue';
import { getInitials } from '../../view-system/utils/displayFormatters';
import { accountInCommandCenter, flowHome, viewInCommandCenter } from '../../../../../mini-apps/_shared/flow-links';
import { ROLE_TONE } from './role-tone';

/** Arrowing through the list fires no request until a row has been held this long. */
export const PREVIEW_SETTLE_MS = 150;

export function impersonationPreviewQuery(apiFetch: ApiFetch, slackUserId: string) {
  return {
    queryKey: ['admin', 'impersonation-preview', slackUserId] as const,
    queryFn: () => fetchImpersonationPreview(apiFetch, slackUserId),
    // A switch reloads the page, so this cache lives exactly as long as the palette's page.
    staleTime: Infinity,
    meta: { suppressErrorToast: true },
  };
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

function meetingDay(at: Date, now: Date): string {
  const days = Math.round((startOfDay(at) - startOfDay(now)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return at.toLocaleDateString('en-US', { weekday: 'short' });
}

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

export function ReportingLine({ managers, name }: { managers: readonly string[]; name?: string }) {
  const steps = name ? [...managers, name] : managers;
  return (
    <span className="flex min-w-0 items-center gap-0.5 overflow-hidden whitespace-nowrap">
      {steps.map((step, i) => (
        // The path down to this step is unique even when two managers share a first name.
        <span key={steps.slice(0, i + 1).join('›')} className="flex items-center gap-0.5">
          {i > 0 && <ChevronRight className="h-3 w-3 shrink-0 text-content-tertiary" aria-hidden="true" />}
          <span className={name && i === steps.length - 1 ? 'font-semibold text-content-secondary' : undefined}>{step}</span>
        </span>
      ))}
    </span>
  );
}

const statClass =
  'inline-flex items-baseline gap-1 rounded-full border border-border-secondary bg-bg-primary px-2.5 py-1 text-xs text-content-secondary';

export function ImpersonationPreviewPane({
  user,
  name,
  disabled,
  onSelect,
  meetingsTab,
  onMeetingsTab,
}: {
  user: OwnerOption;
  name: string;
  disabled: boolean;
  /** Switch to `user`, then open `href` (or reload where you are). */
  onSelect: (href?: string) => void;
  /** Their next meetings or their last ones; the palette holds it so a sort can pick it. */
  meetingsTab: 'next' | 'last';
  onMeetingsTab: (tab: 'next' | 'last') => void;
}) {
  const { apiFetch } = useApi();
  const settledId = useDebouncedValue(user.slackUserId, PREVIEW_SETTLE_MS);
  const { data, isError } = useQuery({
    ...impersonationPreviewQuery(apiFetch, user.slackUserId),
    enabled: settledId === user.slackUserId,
  });
  const role = user.userType ?? 'MEMBER';
  const tone = ROLE_TONE[role];
  const first = name.split(' ')[0] ?? name;
  const [now] = useState(() => new Date());
  const subline = [data?.title ?? USER_TYPE_INFO[role].label, data?.team].filter(Boolean).join(' · ');
  const shown = (meetingsTab === 'next' ? data?.meetings : data?.pastMeetings) ?? [];

  return (
    <div className="flex min-h-0 flex-col gap-3.5 overflow-y-auto bg-bg-secondary px-6 py-5" data-testid="impersonation-preview">
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${tone.avatar}`}>
          {getInitials(name)}
        </span>
        <span className="flex min-w-0 flex-col gap-px">
          <span className="truncate text-[17px] font-semibold text-content-primary">{name}</span>
          <span className="truncate text-[12.5px] text-content-secondary">{subline}</span>
          {user.reportingLine?.length ? (
            <span className="text-xs text-content-tertiary">
              <ReportingLine managers={user.reportingLine} name={first} />
            </span>
          ) : null}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5" aria-live="polite">
        {!data && !isError && [0, 1, 2].map((i) => (
          <span key={i} aria-hidden="true" className="h-[26px] w-24 rounded-full bg-bg-tertiary" />
        ))}
        {data?.accounts != null && (
          <button
            type="button"
            disabled={disabled}
            aria-label={`View as ${first} and open their ${plural(data.accounts, 'account', 'accounts')}`}
            onClick={() => onSelect(flowHome('account'))}
            className={`${statClass} transition-colors hover:border-border-primary hover:bg-bg-primary-hover disabled:opacity-50`}
          >
            <span className="font-semibold tabular-nums text-content-primary">{data.accounts.toLocaleString('en-US')}</span>
            {data.accounts === 1 ? 'account' : 'accounts'}
          </button>
        )}
        {data?.signalsToDo != null && (
          <button
            type="button"
            disabled={disabled}
            aria-label={`View as ${first} and open their ${plural(data.signalsToDo, 'signal', 'signals')} to do`}
            onClick={() => onSelect(viewInCommandCenter('signal', 'signal-my'))}
            className={`${statClass} transition-colors hover:border-border-primary hover:bg-bg-primary-hover disabled:opacity-50`}
          >
            <span className="font-semibold tabular-nums text-content-primary">{data.signalsToDo.toLocaleString('en-US')}</span>
            {data.signalsToDo === 1 ? 'signal to do' : 'signals to do'}
          </button>
        )}
        {data && (
          <span className={statClass}>
            {data.lastActiveAt
              ? <>last active <span className="font-semibold text-content-primary">{formatRelativeTime(String(data.lastActiveAt), now)}</span></>
              : 'no actions in Terra yet'}
          </span>
        )}
      </div>

      <div className="flex flex-col overflow-hidden rounded-[10px] border border-border-secondary bg-bg-primary">
        <div className="flex items-center justify-between px-3.5 pb-1.5 pt-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-content-tertiary">Meetings</span>
          <div role="group" aria-label="Which meetings" className="flex gap-0.5 rounded-[7px] bg-bg-secondary p-0.5">
            {(['next', 'last'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                aria-pressed={meetingsTab === tab}
                onClick={() => onMeetingsTab(tab)}
                className={`h-[22px] rounded-[5px] border px-2.5 text-[11.5px] font-semibold transition-colors ${
                  meetingsTab === tab ? 'border-border-primary bg-bg-primary text-content-primary' : 'border-transparent text-content-secondary hover:text-content-primary'
                }`}
              >
                {tab === 'next' ? 'Next' : 'Last'}
              </button>
            ))}
          </div>
        </div>
        {!data && !isError && [70, 55, 62].map((w) => (
          <div key={w} aria-hidden="true" className="grid grid-cols-[96px_minmax(0,1fr)] gap-2.5 border-t border-border-secondary px-3.5 py-3">
            <span className="h-3 w-16 rounded bg-bg-tertiary" />
            <span className="h-3 rounded bg-bg-tertiary" style={{ width: `${w}%` }} />
          </div>
        ))}
        {isError && (
          <div className="border-t border-border-secondary px-3.5 py-3 text-xs text-content-tertiary">Couldn&apos;t load their details.</div>
        )}
        {data && shown.length === 0 && (
          <div className="border-t border-border-secondary px-3.5 py-3 text-xs text-content-tertiary">
            {meetingsTab === 'next' ? 'No meetings in the next few days.' : 'No meetings in the last few days.'}
          </div>
        )}
        {shown.map((m) => {
          const at = new Date(m.startsAt);
          // The server dedupes on account, title and start, so together they are unique.
          const key = `${m.accountId ?? ''}|${m.title}|${String(m.startsAt)}`;
          const body = (
            <>
              <span className="flex flex-col text-xs text-content-secondary">
                <span className="font-semibold text-content-primary">{meetingDay(at, now)}</span>
                {at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
              <span className="flex min-w-0 flex-col gap-px text-left">
                <span className="truncate text-[13px] font-semibold text-content-primary">{m.accountName ?? 'No account'}</span>
                <span className="truncate text-xs text-content-tertiary">{m.title}</span>
              </span>
            </>
          );
          const rowClass = 'grid grid-cols-[96px_minmax(0,1fr)] gap-2.5 border-t border-border-secondary px-3.5 py-2 text-left';
          return m.accountId ? (
            <button
              key={key}
              type="button"
              disabled={disabled}
              aria-label={`View as ${first} and open ${m.accountName ?? 'the account'}`}
              onClick={() => onSelect(accountInCommandCenter(m.accountId!))}
              className={`${rowClass} transition-colors hover:bg-bg-primary-hover disabled:opacity-50`}
            >
              {body}
            </button>
          ) : (
            <div key={key} className={rowClass}>{body}</div>
          );
        })}
      </div>

      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect()}
        className="mt-auto flex h-10 shrink-0 items-center justify-center gap-2.5 rounded-lg bg-bg-action-inverse text-sm font-semibold text-content-inverse transition-colors hover:bg-bg-action-inverse-hover disabled:opacity-50"
      >
        View as {first}
        <CornerDownLeft className="h-3.5 w-3.5 text-content-inverse-subtle" aria-hidden="true" />
      </button>
    </div>
  );
}
